import { signal, inject, Service } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ChecklistSelection,
  StorageService,
} from '../modules/checklist/services/storage.service';
import {
  Checklist,
  ChecklistItem,
} from '../modules/checklist/types/ChecklistTypes';
import { findItem } from '../modules/checklist/utils/checklist-patches';
import { synthesizeRepeatableOccurrence } from '../modules/checklist/utils/checklist-instantiation';
import { ChecklistHttpService } from './checklist-http.service';
import { ChecklistStoreService } from './checklist-store.service';
import { ChecklistLoaderService } from './checklist-loader.service';
import { ChecklistWriteSenderService } from './checklist-write-sender.service';
import { NetworkStatusService } from './offline/network-status.service';
import { OfflineStorageService } from './offline/offline-storage.service';

export interface MaterializationResult {
  resolvedItemId: string;
  occurrence: ChecklistItem;
  sectionId: string;
  customInputIdMap: Record<string, string> | null;
}

export interface WriteTarget {
  checklist: Checklist;
  sectionId: string;
  itemId: string;
  customInputIdMap: Record<string, string> | null;
}

@Service()
export class RepeatableMaterializerService {
  private readonly store = inject(ChecklistStoreService);
  private readonly loader = inject(ChecklistLoaderService);
  private readonly writeSender = inject(ChecklistWriteSenderService);
  private readonly storageService = inject(StorageService);
  private readonly checklistHttpService = inject(ChecklistHttpService);
  private readonly networkStatusService = inject(NetworkStatusService);
  private readonly offlineStorageService = inject(OfflineStorageService);

  private readonly _materializingPrototype = signal<string | null>(null);

  private readonly inFlightMaterializations = new Map<
    string,
    Promise<MaterializationResult>
  >();

  readonly materializingPrototype = this._materializingPrototype.asReadonly();

  // ChecklistService registers this at construction (it injects this service), avoiding a circular import.
  private selectionHandler:
    | ((sectionId: string, item: ChecklistItem) => void)
    | null = null;

  registerSelectionHandler(
    handler: (sectionId: string, item: ChecklistItem) => void
  ): void {
    this.selectionHandler = handler;
  }

  async resolveTargetForWrite(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<WriteTarget> {
    const refreshedAtEntry = this.store.find(checklist.id) ?? checklist;
    const prototype = findItem(refreshedAtEntry, sectionId, itemId);
    if (!prototype || !prototype.isRepeatablePrototype) {
      return {
        checklist: refreshedAtEntry,
        sectionId,
        itemId,
        customInputIdMap: null,
      };
    }

    const result = await this.materializePrototype(
      refreshedAtEntry,
      sectionId,
      prototype
    );
    const refreshedChecklist =
      this.store.find(refreshedAtEntry.id) ?? refreshedAtEntry;

    return {
      checklist: refreshedChecklist,
      sectionId: result.sectionId,
      itemId: result.resolvedItemId,
      customInputIdMap: result.customInputIdMap,
    };
  }

  materializePrototypeIfNeeded(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): Promise<MaterializationResult | null> {
    const prototype = findItem(checklist, sectionId, itemId);
    if (!prototype || !prototype.isRepeatablePrototype) {
      return Promise.resolve(null);
    }
    return this.materializePrototype(checklist, sectionId, prototype);
  }

  requireOccurrenceItemId(
    checklist: Checklist,
    sectionId: string,
    itemId: string
  ): void {
    const item = findItem(checklist, sectionId, itemId);
    if (item?.isRepeatablePrototype) {
      const message = `Refusing to send write to repeatable prototype id ${itemId}; gateway should have materialized an occurrence first.`;
      console.error(message);
      throw new Error(message);
    }
  }

  private materializePrototype(
    checklist: Checklist,
    sectionId: string,
    prototype: ChecklistItem
  ): Promise<MaterializationResult> {
    const key = `${checklist.id}:${sectionId}:${prototype.id}`;
    const existing = this.inFlightMaterializations.get(key);
    if (existing) {
      return existing;
    }

    this._materializingPrototype.set(prototype.id);

    const work = (async () => {
      try {
        return await this.runMaterialization(checklist, sectionId, prototype);
      } finally {
        this.inFlightMaterializations.delete(key);
        if (this._materializingPrototype() === prototype.id) {
          this._materializingPrototype.set(null);
        }
      }
    })();

    this.inFlightMaterializations.set(key, work);
    return work;
  }

  private async runMaterialization(
    checklist: Checklist,
    sectionId: string,
    prototype: ChecklistItem
  ): Promise<MaterializationResult> {
    checklist = this.store.find(checklist.id) ?? checklist;
    prototype = findItem(checklist, sectionId, prototype.id) ?? prototype;
    const { occurrence, idTree, customInputIdMap, patchedChecklist } =
      synthesizeRepeatableOccurrence(checklist, sectionId, prototype);

    const prevChecklists = this.store.checklists();
    const prevSelectedChecklist = this.store.selectedChecklist();
    const prevLastSelection = this.storageService.getChecklistLastSelection(
      checklist.id
    ) as ChecklistSelection | undefined;

    this.store.replace(patchedChecklist);
    this.applyOccurrenceSelection(checklist.id, sectionId, occurrence);

    const result: MaterializationResult = {
      resolvedItemId: occurrence.id,
      occurrence,
      sectionId,
      customInputIdMap,
    };

    if (this.store.isIsolatedPreview(checklist.id)) {
      return result;
    }

    const isOnline = this.networkStatusService.online();
    if (!isOnline) {
      await this.offlineStorageService.addAction(
        this.checklistHttpService.addRepeatableChecklistItem,
        checklist.id,
        sectionId,
        prototype.id,
        idTree
      );
      return result;
    }

    try {
      await firstValueFrom(
        this.checklistHttpService.addRepeatableChecklistItem(
          checklist.id,
          sectionId,
          prototype.id,
          idTree
        )
      );
    } catch (error) {
      if (this.writeSender.isNonQueueableHttpFailure(error)) {
        this.rollbackOptimisticPatch(
          checklist.id,
          sectionId,
          prototype,
          prevChecklists,
          prevSelectedChecklist,
          prevLastSelection
        );
        console.error(error);
        throw error;
      }
      await this.offlineStorageService.addAction(
        this.checklistHttpService.addRepeatableChecklistItem,
        checklist.id,
        sectionId,
        prototype.id,
        idTree
      );
      return result;
    }

    this.loader.refreshChecklistAfterWrite(checklist.id);
    return result;
  }

  private rollbackOptimisticPatch(
    checklistId: string,
    sectionId: string,
    prototype: ChecklistItem,
    prevChecklists: Checklist[],
    prevSelectedChecklist: Checklist | null,
    prevLastSelection: ChecklistSelection | undefined
  ): void {
    this.store.restore(checklistId, prevChecklists, prevSelectedChecklist);
    if (prevLastSelection) {
      this.storageService.saveChecklistLastSelection(
        checklistId,
        prevLastSelection
      );
    } else {
      this.storageService.clearChecklistLastSelection(checklistId);
    }
    this.selectionHandler?.(sectionId, prototype);
  }

  private applyOccurrenceSelection(
    checklistId: string,
    sectionId: string,
    occurrence: ChecklistItem
  ): void {
    // Persist the selection so a re-run of ChecklistService.loadChecklist restores the new occurrence,
    // not the prototype.
    this.storageService.saveChecklistLastSelection(checklistId, {
      sectionId,
      itemId: occurrence.id,
    });

    this.selectionHandler?.(sectionId, occurrence);
  }
}
